<template>
  <footer class="text-white">
    <div class="top-section">
      <div class="container d-flex flex-column flex-md-row justify-content-between">
        <div class="mb-3 mb-md-0">
          <img
            :src="logoImageUrl"
            alt="Logo"
            height="120"
            class="me-2"
            @error="$event.target.style.display='none'"
          >
        </div>
        <div class="address-details">
          <div class="mb-2">
            <h5><i class="bi bi-geo-alt-fill me-2" />Address</h5>
            <p>
              {{ styleStore.storeInfo?.addresses?.address1 || '' }}<br>
              {{ styleStore.storeInfo?.addresses?.address2 || '' }}<br>
              {{ styleStore.storeInfo?.addresses?.address3 || '' }}<br>
              {{ styleStore.storeInfo?.addresses?.country || '' }}<br>
              {{ styleStore.storeInfo?.addresses?.postal_code || '' }}
            </p>
          </div>
        </div>
        <!-- Contact Info -->
        <div class="contact-details">
          <div class="d-flex mb-2">
            <h5>Contact</h5>
          </div>
          <div class="d-flex  mb-2">
            <i class="bi bi-telephone-fill me-2" />
            <span>{{ styleStore.storeInfo?.phone }}</span>
          </div>
          <div class="d-flex ">
            <i class="bi bi-envelope-fill me-2" />
            <span>{{ styleStore.generalInfo?.email || styleStore.storeInfo?.email }}</span>
          </div>
        </div>
      </div>
    </div>
    
    <div class="bottom-section bg-dark">
      <button
        type="button"
        class="btn btn-primary"
        data-bs-toggle="modal"
        data-bs-target="#check-balance-modal"
      >
        CHECK YOUR BALANCE
      </button>
    </div>
  </footer>

  <!-- Modal -->
  <div
    id="check-balance-modal"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="check-balance-modalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h1
            id="check-balance-modalLabel"
            class="modal-title"
          >
            CHECK YOUR BALANCE
          </h1>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          />
        </div>

        <div class="modal-body">
          <!-- Voucher Code Input -->
          <b-form-group
            id="fieldset-1"
            label="Enter Voucher Code"
            label-for="input-1"
          >
            <b-form-input
              id="input-1"
              v-model="voucherCode"
              trim
            />
          </b-form-group>

          <!-- Captcha Section -->
          <b-form-group>
            <template #label>
              Captcha: {{ captcha.question }}
            </template>
            <b-form-input
              v-model="userAnswer"
              placeholder="Enter your answer"
            />
            <small
              class="text-muted mt-2 d-block"
              role="button"
              style="cursor: pointer"
              @click="reloadCaptcha"
            >
              Reload CAPTCHA
            </small>
          </b-form-group>
          <small
            v-if="error"
            class="text-danger"
          >{{ error }}</small>
          <div
            v-if="balance !== null"
            class="alert alert-success mt-3"
          >
            Your balance is: <strong>{{ `${styleStore.storeInfo.currency} ${balance}` }}</strong><br>
            Expires on: <strong>{{ formatDate(expiresAt) }}</strong>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!isFormValid"
            @click="handleSubmit"
          >
            <i class="bi bi-check-square-fill me-2" /> CHECK YOUR BALANCE
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import axios from 'axios'
import { useStyleStore } from '../stores/styleStore'

const styleStore = useStyleStore()
const logoImageUrl = computed(() => styleStore.logoImageUrl)

const voucherCode = ref('')
const userAnswer = ref('')
const error = ref('')
const balance = ref(null)
const expiresAt = ref(null)

const captcha = ref(generateCaptcha())
function resetForm() {
  voucherCode.value = ''
  userAnswer.value = ''
  error.value = ''
  balance.value = null
  expiresAt.value = null
  captcha.value = generateCaptcha()
}
function generateCaptcha() {
  const a = Math.floor(Math.random() * 10) + 1
  const b = Math.floor(Math.random() * 10) + 1
  return {
    question: `What is ${a} + ${b}?`,
    answer: a + b
  }
}

function reloadCaptcha() {
  captcha.value = generateCaptcha()
  userAnswer.value = ''
  error.value = ''
  balance.value = null
  expiresAt.value = null
}

const isCaptchaCorrect = computed(() => {
  return parseInt(userAnswer.value) === captcha.value.answer
})

const isFormValid = computed(() => {
  return voucherCode.value.trim().length > 0 && isCaptchaCorrect.value
})
onMounted(() => {
  const modalEl = document.getElementById('check-balance-modal')
  if (modalEl) {
    modalEl.addEventListener('hidden.bs.modal', resetForm)
  }
})

onBeforeUnmount(() => {
  const modalEl = document.getElementById('check-balance-modal')
  if (modalEl) {
    modalEl.removeEventListener('hidden.bs.modal', resetForm)
  }
})
async function handleSubmit() {
  if (!isFormValid.value) {
    error.value = 'Please correct the form before submitting.'
    return
  }

  error.value = ''
  balance.value = null
  expiresAt.value = null

  try {
    const response = await axios.post(
      `${process.env.VUE_APP_API_URL}/api/v1/promotions/check_voucher_balance`,
      {
        voucher_no: voucherCode.value.trim()
      }
    )

    if (response.data && typeof response.data.balance !== 'undefined') {
      balance.value = response.data.balance
      expiresAt.value = response.data.expires_at
    } else if (response.data && response.data.error) {
      error.value = response.data.error
    } else {
      error.value = 'Unexpected response. Please try again.'
    }
  } catch (err) {
    console.error('API Error:', err.response?.data || err.message || err)
    if (err.response?.data?.error) {
      error.value = err.response.data.error
    } else {
      error.value = 'Failed to check balance. Please try again later.'
    }
  }
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}
</script>






<style scoped>
.modal-header {
  background-image: url("/src/images/order-bg.png");
  background-size: cover;
  background-position: center;
  padding: 30px 10px;
  width: 100%;
  justify-content: center;
}
.modal-title {
  font-size: 26px;
  font-weight: 600;
  color: #4e5b6a;
}
.btn-close{
  position: absolute;
  top: 15px;
  right: 20px;
}
.modal-footer{
  justify-content: center;
}
.address-details{
  color: #000;
}
.contact-details{
  color: #000;
}
.top-section{
padding: 30px 0 30px
}
.bottom-section{
  padding: 25px;
    display: flex;
    justify-content: center;
}
</style>

